import React, { ChangeEvent, memo, useState } from 'react';
import { Col, Alert, Collapse } from 'antd';
import {
  ExclamationCircleOutlined,
  DownOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { FieldConfig } from '../types/fieldTypes';
import { TextField } from './fields/TextField';
import { NumberField } from './fields/NumberField';
import { SelectField } from './fields/SelectField';
import { MultiSelectField } from './fields/MultiSelectField';
import { TextAreaField } from './fields/TextAreaField';
import { DateField } from './fields/DateField';
import { CheckboxField } from './fields/CheckboxField';
import { ImageUploadField } from './fields/ImageUploadField';
import { YouTubeVideoField } from './fields/YouTubeVideoField';
import { PriceEvaluationField } from './fields/PriceEvaluationField';
import { ArrayField } from './fields/ArrayField';
import { CardsField } from './fields/CardsField';

interface DynamicFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  mode?: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const DynamicField: React.FC<DynamicFieldProps> = memo(
  ({ field, value, onChange, mode = 'edit', formData }) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <TextField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'number':
        return (
          <NumberField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'select':
        return (
          <SelectField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'multiselect':
        return (
          <MultiSelectField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'textarea':
        return (
          <TextAreaField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'date':
        return (
          <DateField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'checkbox':
        return (
          <CheckboxField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'imageUpload':
        return (
          <ImageUploadField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'youtubeVideo':
        return (
          <YouTubeVideoField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'priceEvaluation':
        return (
          <PriceEvaluationField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'array':
        return (
          <ArrayField
            field={field}
            value={value}
            onChange={(value: any[]) => {
              const customEvent = {
                target: {
                  name: field.name,
                  value: value,
                },
                nativeEvent: {} as any,
                currentTarget: {} as any,
                bubbles: false,
                cancelable: false,
                defaultPrevented: false,
                eventPhase: 0,
                isTrusted: false,
                preventDefault: () => {},
                isDefaultPrevented: () => false,
                stopPropagation: () => {},
                isPropagationStopped: () => false,
                persist: () => {},
                timeStamp: Date.now(),
                type: 'change',
              } as any;
              onChange(customEvent);
            }}
            mode={mode}
            formData={formData}
          />
        );

      case 'cards':
        return (
          <CardsField
            field={field}
            value={value}
            onChange={onChange}
            mode={mode}
            formData={formData}
          />
        );

      case 'description':
        // Handle dynamic content for selected package display
        const getDescriptionContent = () => {
          if (
            field.customProps?.dynamicContent &&
            field.name === 'selectedPackage'
          ) {
            // Get selected package from form data
            const selectedPackage = formData?.selectedPackage;
            if (selectedPackage && typeof selectedPackage === 'object') {
              return `Selected Package: ${selectedPackage.name}
Price: ${selectedPackage.price} ALL/month
Description: ${selectedPackage.description}

You need to purchase a Western Union token worth ${selectedPackage.price} ALL to activate this package.`;
            } else if (selectedPackage && typeof selectedPackage === 'string') {
              return `Selected Package: ${selectedPackage}
Please check your package selection to see the exact price.`;
            } else {
              return 'No package selected. Please go back and select a package first.';
            }
          }
          return field.customProps?.content || '';
        };

        return (
          <Col span={field.layout?.span || 24}>
            <div
              style={{
                padding: '16px',
                backgroundColor:
                  field.name === 'selectedPackage' ? '#e6f7ff' : '#f6f8fa',
                border:
                  field.name === 'selectedPackage'
                    ? '1px solid #91d5ff'
                    : '1px solid #e1e4e8',
                borderRadius: '6px',
                marginBottom: '16px',
              }}
            >
              <div
                style={{
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                  color: '#24292e',
                  fontSize: '14px',
                  fontWeight:
                    field.name === 'selectedPackage' ? '500' : 'normal',
                }}
              >
                {getDescriptionContent()}
              </div>
            </div>
          </Col>
        );

      case 'collapsible':
        return (
          <Col span={field.layout?.span || 24}>
            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f6f8fa',
                  border: '1px solid #e1e4e8',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'background-color 0.2s ease',
                }}
                onClick={() => setIsCollapsed(!isCollapsed)}
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor = '#e1e4e8')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor = '#f6f8fa')
                }
              >
                <span
                  style={{
                    fontWeight: '500',
                    color: '#24292e',
                    fontSize: '14px',
                  }}
                >
                  {field.customProps?.collapsibleTitle ||
                    'Show More Information'}
                </span>
                {isCollapsed ? (
                  <RightOutlined
                    style={{ color: '#586069', fontSize: '12px' }}
                  />
                ) : (
                  <DownOutlined
                    style={{ color: '#586069', fontSize: '12px' }}
                  />
                )}
              </div>

              {!isCollapsed && (
                <div
                  style={{
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e1e4e8',
                    borderTop: 'none',
                    borderRadius: '0 0 6px 6px',
                    borderTopLeftRadius: '0',
                    borderTopRightRadius: '0',
                  }}
                >
                  <div
                    style={{
                      whiteSpace: 'pre-line',
                      lineHeight: '1.6',
                      color: '#24292e',
                      fontSize: '14px',
                    }}
                  >
                    {field.customProps?.collapsibleContent || ''}
                  </div>
                </div>
              )}
            </div>
          </Col>
        );

      default:
        return (
          <Col span={24}>
            <Alert
              message={`Unsupported field type: ${field.type}`}
              type="warning"
              icon={<ExclamationCircleOutlined />}
              showIcon
            />
          </Col>
        );
    }
  }
);

DynamicField.displayName = 'DynamicField';
